import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  Item,
  ItemDescription,
  ItemHeader,
  ItemTitle,
  Spinner,
  Table,
  TableColumn,
} from '@ibsys/kit';
import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  IntegrationScenario,
  IntegrationScenarioRunResponse,
  IntegrationScenarioStepResult,
  useIntegrationScenarios,
  useRunIntegrationScenario,
} from '@/entities/integrations';
import { useHasPermission } from '@/entities/profile';

type Props = {
  code: string;
  mockMode: boolean;
};

export const ScenariosSection: FC<Props> = ({ code, mockMode }) => {
  const { t } = useTranslation(['integrations']);
  const canAdmin = useHasPermission('ADMIN_INTEGRATION_INFO');
  const { data, isLoading } = useIntegrationScenarios(code);
  const { mutateAsync, isPending, variables } = useRunIntegrationScenario();

  const [results, setResults] = useState<Record<string, IntegrationScenarioRunResponse>>({});
  const [confirmTarget, setConfirmTarget] = useState<IntegrationScenario | null>(null);

  const runScenario = async (scenario: IntegrationScenario, confirmProduction: boolean) => {
    try {
      const response = await mutateAsync({
        code,
        scenarioCode: scenario.code,
        payload: { confirmProduction },
      });
      setResults((prev) => ({ ...prev, [scenario.code]: response }));
    } catch {
      // Реальную причину сбоя запуска сценария уже показывает глобальный обработчик ответов API
      // (см. shared/api/errorHandler). Здесь только гасим отклонение промиса, не дублируя тост
      // несвязанным сообщением о сохранении сведений об интеграции.
    }
  };

  const handleRun = (scenario: IntegrationScenario) => {
    if (!mockMode && !scenario.safeInProduction) {
      setConfirmTarget(scenario);
      return;
    }
    void runScenario(scenario, false);
  };

  const handleConfirmProduction = async () => {
    if (!confirmTarget) return;
    const target = confirmTarget;
    setConfirmTarget(null);
    await runScenario(target, true);
  };

  const scenarios = data?.scenarios ?? [];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-3 text-xl">
          <span>{t('integrations:scenarios.title')}</span>
          <Badge variant={mockMode ? 'primary' : 'destructive'}>
            {mockMode ? t('integrations:mockMode.mock') : t('integrations:mockMode.production')}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {isLoading && <Spinner className="size-5" />}
        {!isLoading && scenarios.length === 0 && (
          <div className="text-muted-foreground">{t('integrations:scenarios.empty')}</div>
        )}
        {scenarios.map((scenario) => {
          const isRunning = isPending && variables?.scenarioCode === scenario.code;
          const result = results[scenario.code];
          return (
            <ScenarioItem
              key={scenario.code}
              scenario={scenario}
              isRunning={isRunning}
              canRun={canAdmin}
              onRun={() => handleRun(scenario)}
              result={result}
            />
          );
        })}
      </CardContent>

      <DialogRoot
        open={!!confirmTarget}
        onOpenChange={(open) => {
          if (!open) setConfirmTarget(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('integrations:scenarios.confirmProductionTitle')}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">{t('integrations:scenarios.confirmProductionDescription')}</p>
          <DialogFooter>
            <div className="flex gap-3 justify-end w-full">
              <Button variant="ghost" onClick={() => setConfirmTarget(null)}>
                {t('integrations:scenarios.cancel')}
              </Button>
              <Button onClick={handleConfirmProduction}>{t('integrations:scenarios.confirm')}</Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </DialogRoot>
    </Card>
  );
};

type ScenarioItemProps = {
  scenario: IntegrationScenario;
  isRunning: boolean;
  canRun: boolean;
  onRun: () => void;
  result?: IntegrationScenarioRunResponse;
};

const ScenarioItem: FC<ScenarioItemProps> = ({ scenario, isRunning, canRun, onRun, result }) => {
  const { t } = useTranslation(['integrations']);

  const stepColumns: TableColumn<IntegrationScenarioStepResult>[] = useMemo(
    () => [
      {
        accessorKey: 'step',
        header: () => <span>{t('integrations:scenarios.result.step')}</span>,
        size: 80,
        cell: ({ row }) => <span className="text-sm">{row.original.step}</span>,
      },
      {
        accessorKey: 'method',
        header: () => <span>{t('integrations:scenarios.result.method')}</span>,
        cell: ({ row }) => <span className="font-mono text-sm">{row.original.method}</span>,
      },
      {
        accessorKey: 'responseStatus',
        header: () => <span>{t('integrations:scenarios.result.status')}</span>,
        size: 100,
        cell: ({ row }) => {
          const code = row.original.responseStatus;
          const isOk = code >= 200 && code < 300;
          return <Badge variant={isOk ? 'primary' : 'destructive'}>{code}</Badge>;
        },
      },
      {
        accessorKey: 'durationMs',
        header: () => <span>{t('integrations:scenarios.result.duration')}</span>,
        size: 110,
        cell: ({ row }) => <span className="text-sm">{row.original.durationMs}</span>,
      },
      {
        accessorKey: 'responseSizeBytes',
        header: () => <span>{t('integrations:scenarios.result.size')}</span>,
        size: 110,
        cell: ({ row }) => <span className="text-sm">{row.original.responseSizeBytes}</span>,
      },
      {
        accessorKey: 'error',
        header: () => <span>{t('integrations:scenarios.result.errorColumn')}</span>,
        cell: ({ row }) => <span className="text-sm text-destructive">{row.original.error ?? ''}</span>,
      },
    ],
    [t],
  );

  return (
    <Item variant="outline" size="sm">
      <ItemHeader>
        <ItemTitle className="flex flex-wrap items-center gap-2">
          <span>{scenario.name}</span>
          <Badge variant={scenario.safeInProduction ? 'primary' : 'secondary'}>
            {scenario.safeInProduction ? t('integrations:scenarios.safe') : t('integrations:scenarios.unsafe')}
          </Badge>
          <span className="text-xs text-muted-foreground">
            {t('integrations:scenarios.estimatedRequestsLabel', { count: scenario.estimatedRequests })}
          </span>
        </ItemTitle>
      </ItemHeader>
      <ItemDescription className="text-sm text-muted-foreground">{scenario.description}</ItemDescription>
      <ol className="list-decimal list-inside text-sm text-muted-foreground pl-2">
        {scenario.steps.map((step, idx) => (
          <li key={`${scenario.code}-step-${idx}`} className="font-mono">
            {step}
          </li>
        ))}
      </ol>
      {canRun && (
        <div className="mt-2">
          <Button onClick={onRun} disabled={isRunning} size="sm">
            {isRunning ? (
              <span className="flex items-center gap-2">
                <Spinner className="size-4" />
                {t('integrations:scenarios.running')}
              </span>
            ) : (
              t('integrations:scenarios.run')
            )}
          </Button>
        </div>
      )}
      {result && (
        <div className="mt-3 flex flex-col gap-2 rounded-md border p-3">
          <div className="flex flex-wrap items-center gap-3">
            <Badge variant={result.status === 'success' ? 'primary' : 'destructive'}>
              {result.status === 'success'
                ? t('integrations:scenarios.result.success')
                : t('integrations:scenarios.result.error')}
            </Badge>
            <span className="text-xs text-muted-foreground">
              {t('integrations:scenarios.result.durationValue', { value: result.durationMs })}
            </span>
            <span className="text-xs text-muted-foreground">
              {t('integrations:scenarios.result.totalRequestsValue', { count: result.totalRequests })}
            </span>
          </div>
          <Table
            data={result.steps}
            columns={stepColumns}
            getKey={(row) => `${result.scenarioCode}-${row.step}`}
            styles={{ size: 'sm' }}
          />
        </div>
      )}
    </Item>
  );
};
